import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import {
  ArrowLeft, Pill, AlertTriangle, Clock,
  Shield, Brain, Activity, Heart, Zap, CheckCircle2,
  Info, Utensils, Dumbbell, Thermometer,
  BookOpen, FlaskConical, Stethoscope, Bone, Droplets
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';

const ARTHRITIS_PROTOCOL = [
  {
    name: 'Glucosamine + Chondroitin',
    dosage: '1,500mg Glucosamine + 1,200mg Chondroitin daily',
    timing: 'With meals, split into 2-3 doses',
    icon: Bone,
    color: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    evidence: 'A',
    why: 'Glucosamine supports cartilage repair and slows joint degeneration. Chondroitin helps retain water in cartilage for shock absorption. The GAIT trial showed the combination significantly reduced pain in moderate-to-severe knee OA. Effects typically take 4-8 weeks to become noticeable.',
    interactions: ['May enhance blood thinners (warfarin) — monitor INR', 'Safe with most NSAIDs', 'Shellfish-derived glucosamine — avoid if allergic (use plant-based alternative)'],
    notes: 'Glucosamine sulfate is the best-studied form. Take consistently for 3+ months before judging effectiveness. Consider sulfate form specifically.',
    conditions: ['OA', 'RA']
  },
  {
    name: 'Turmeric / Curcumin (with Piperine)',
    dosage: '500-1,000mg curcumin, 2-3 times daily',
    timing: 'With meals containing fat for absorption',
    icon: Zap,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    evidence: 'A',
    why: 'Curcumin is a powerful anti-inflammatory that inhibits NF-kB and COX-2 — the same pathways targeted by prescription anti-inflammatories. Multiple RCTs show it rivals ibuprofen for OA pain relief with fewer GI side effects. For RA, it modulates the immune response driving joint destruction.',
    interactions: ['May enhance blood thinners — monitor if on anticoagulants', 'Piperine (black pepper extract) increases absorption by 2,000%', 'May interact with diabetes medications (lowers blood sugar)'],
    notes: 'Look for formulations with piperine/BioPerine or liposomal curcumin for best absorption. Standard turmeric spice has very low curcumin content.',
    conditions: ['OA', 'RA']
  },
  {
    name: 'Omega-3 (High EPA)',
    dosage: '2,000-4,000mg combined EPA/DHA (prioritize EPA)',
    timing: 'With meals containing fat, split AM/PM',
    icon: Heart,
    color: 'bg-rose-100 text-rose-700 border-rose-200',
    evidence: 'A',
    why: 'EPA directly reduces inflammatory cytokines (TNF-alpha, IL-6) that drive joint inflammation in both OA and RA. Multiple meta-analyses show fish oil reduces morning stiffness and joint tenderness in RA, and may allow patients to reduce NSAID use.',
    interactions: ['May enhance blood thinners — monitor INR', 'Safe with DMARDs and biologics', 'High doses may lower blood pressure slightly'],
    notes: 'Choose IFOS-certified fish oil for purity. EPA is more anti-inflammatory than DHA — look for high-EPA formulas. Algae-based available for fish allergy.',
    conditions: ['OA', 'RA']
  },
  {
    name: 'Collagen (Type II)',
    dosage: '10-40mg undenatured Type II collagen (UC-II) daily',
    timing: 'On empty stomach, ideally before bed',
    icon: Bone,
    color: 'bg-violet-100 text-violet-700 border-violet-200',
    evidence: 'B+',
    why: 'UC-II works differently from regular collagen — it retrains the immune system to stop attacking joint cartilage (oral tolerance). Clinical trials show UC-II outperformed glucosamine + chondroitin for knee OA. For RA, it helps reduce the autoimmune attack on joints.',
    interactions: ['Safe with most arthritis medications', 'Different from hydrolyzed collagen (which is for skin/hair)', 'Take separately from other supplements for best immune tolerance effect'],
    notes: 'UC-II (undenatured Type II) is NOT the same as hydrolyzed collagen peptides. The dose is much smaller (40mg vs 10,000mg) because it works through immune modulation, not structural support.',
    conditions: ['OA', 'RA']
  },
  {
    name: 'Boswellia Serrata (5-LOXIN)',
    dosage: '100-250mg standardized extract, twice daily',
    timing: 'With meals',
    icon: Shield,
    color: 'bg-green-100 text-green-700 border-green-200',
    evidence: 'B+',
    why: 'Boswellia inhibits 5-lipoxygenase (5-LOX), blocking leukotriene production — a key inflammatory pathway in arthritis. Clinical trials show significant pain reduction within 7 days. Particularly effective for knee OA with improvements in walking distance and joint flexion.',
    interactions: ['May enhance effects of anti-inflammatory medications', 'Safe with most arthritis treatments', 'Rare GI upset at high doses'],
    notes: 'Look for 5-LOXIN or ApresFlex standardized extracts (at least 30% AKBA). These are significantly more potent than generic boswellia.',
    conditions: ['OA', 'RA']
  },
  {
    name: 'Vitamin D3 + K2',
    dosage: '2,000-5,000 IU D3 + 100-200mcg K2 (MK-7)',
    timing: 'Morning with a fat-containing meal',
    icon: Activity,
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    evidence: 'B+',
    why: 'Vitamin D deficiency is strongly linked to both OA progression and RA disease activity. D3 modulates immune function (critical for RA) and supports calcium absorption for bone health around affected joints. K2 directs calcium to bones and away from soft tissue/joints.',
    interactions: ['Safe with DMARDs and biologics', 'K2 may interact with warfarin — consult doctor', 'Enhances calcium absorption'],
    notes: 'Get D3 levels tested — aim for 40-60 ng/mL. K2 as MK-7 has the longest half-life and best evidence. Many arthritis patients are severely D-deficient.',
    conditions: ['OA', 'RA']
  },
  {
    name: 'SAMe (S-Adenosyl-L-Methionine)',
    dosage: '600-1,200mg daily',
    timing: 'On empty stomach, split into 2-3 doses',
    icon: Brain,
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    evidence: 'B',
    why: 'SAMe supports cartilage repair by stimulating proteoglycan and collagen synthesis. Multiple studies show it matches ibuprofen for OA pain relief with sustained use (2+ months). Also helps with the depression that often accompanies chronic arthritis pain.',
    interactions: ['Avoid with antidepressants (SSRIs, MAOIs) — risk of serotonin syndrome', 'Avoid in bipolar disorder', 'Safe with most arthritis medications'],
    notes: 'Effects take 4-8 weeks — be patient. Enteric-coated tablets reduce GI upset. Start low (400mg) and increase gradually.',
    conditions: ['OA']
  },
  {
    name: 'Magnesium Glycinate',
    dosage: '300-400mg elemental magnesium daily',
    timing: 'Evening, 1-2 hours before bed',
    icon: Activity,
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    evidence: 'B',
    why: 'Magnesium reduces inflammation, supports muscle relaxation around affected joints, and improves sleep quality — all critical for arthritis management. Low magnesium is associated with higher CRP (inflammation marker) and worse arthritis outcomes.',
    interactions: ['Separate from antibiotics by 2+ hours', 'May enhance muscle relaxants', 'Safe with DMARDs'],
    notes: 'Glycinate form is best absorbed and least likely to cause digestive issues. Helps with the muscle tension and sleep disruption common in arthritis.',
    conditions: ['OA', 'RA']
  }
];

const COMMON_MEDICATIONS = [
  { name: 'Methotrexate (RA)', notes: 'All supplements are generally safe. Omega-3 and turmeric may enhance anti-inflammatory effects. Ensure adequate folate (methotrexate depletes it). Avoid high-dose Vitamin A.' },
  { name: 'Humira / Enbrel (Biologics)', notes: 'All supplements are safe. Vitamin D is especially important as biologics may affect immune function. Curcumin and omega-3 may provide additional anti-inflammatory support.' },
  { name: 'NSAIDs (Ibuprofen, Naproxen)', notes: 'Curcumin and omega-3 may allow NSAID dose reduction over time (discuss with doctor). Glucosamine is safe alongside. Magnesium can help with NSAID-related GI protection.' },
  { name: 'Prednisone (Corticosteroids)', notes: 'Vitamin D + K2 are especially important — steroids accelerate bone loss. Magnesium depleted by steroids. All supplements safe to combine.' },
  { name: 'Hydroxychloroquine (Plaquenil)', notes: 'All supplements are safe. No known significant interactions. Continue standard supplement protocol.' },
  { name: 'Celebrex (Celecoxib)', notes: 'Safe with all supplements. Curcumin works on similar pathways — combined effect may be beneficial. Monitor for any increased bleeding with omega-3.' }
];

const LIFESTYLE_TIPS = [
  {
    icon: Dumbbell,
    title: 'Low-Impact Exercise',
    content: 'Swimming, cycling, yoga, and walking protect joints while building supporting muscle. Aim for 150 min/week. Water exercise is ideal — buoyancy reduces joint stress by 90%. Avoid high-impact activities during flares.'
  },
  {
    icon: Utensils,
    title: 'Anti-Inflammatory Diet',
    content: 'Emphasize fatty fish (salmon, sardines), colorful vegetables, berries, nuts, olive oil, and leafy greens. Minimize sugar, processed foods, red meat, and refined carbs. The Mediterranean diet reduces RA disease activity in clinical trials.'
  },
  {
    icon: Thermometer,
    title: 'Heat & Cold Therapy',
    content: 'Warm compresses or paraffin baths for morning stiffness (15-20 min). Cold packs for acute inflammation and swelling (10-15 min). Alternate between heat and cold for the best pain relief. Never apply ice directly to skin.'
  },
  {
    icon: Activity,
    title: 'Joint Protection',
    content: 'Use larger joints for heavy tasks, avoid prolonged gripping, and take frequent breaks. Ergonomic tools (jar openers, built-up handles) reduce strain. Maintain optimal weight — every pound lost removes 4 pounds of knee joint stress.'
  },
  {
    icon: Brain,
    title: 'Stress Management & Sleep',
    content: 'Chronic stress worsens inflammation and RA flares. Practice mindfulness, deep breathing, or progressive muscle relaxation daily. Prioritize 7-9 hours of quality sleep — poor sleep directly increases pain sensitivity and inflammatory markers.'
  }
];

const EvidenceGrade = ({ grade }) => {
  const colors = {
    'A': 'bg-green-100 text-green-800 border-green-300',
    'B+': 'bg-blue-100 text-blue-800 border-blue-300',
    'B': 'bg-sky-100 text-sky-800 border-sky-300'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${colors[grade] || colors['B']}`} data-testid={`evidence-grade-${grade}`}>
      Evidence: {grade}
    </span>
  );
};

export default function ArthritisGuidePage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary" data-testid="arthritis-guide-back">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="small" linkTo="/" />
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="arthritis-guide-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-4">
            <Bone className="w-8 h-8 text-cyan-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">
            Arthritis Support Guide
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto text-sm sm:text-base">
            An evidence-based supplement protocol for Osteoarthritis (OA) and Rheumatoid Arthritis (RA) — covering joint repair, inflammation control, and pain management.
          </p>
          <div className="flex gap-2 justify-center mt-4">
            <span className="px-3 py-1 rounded-full bg-cyan-50 text-cyan-700 text-xs font-medium border border-cyan-200">Osteoarthritis</span>
            <span className="px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-medium border border-orange-200">Rheumatoid Arthritis</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8" data-testid="arthritis-guide-disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">Important Medical Disclaimer</p>
              <p className="text-xs text-amber-800">
                This guide is for educational purposes only and does not replace medical advice. Always discuss supplement changes with your rheumatologist or primary care physician,
                especially regarding interactions with DMARDs, biologics, or other arthritis medications.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Pill className="w-5 h-5 text-primary" />
            Arthritis Supplement Protocol
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            8 evidence-based supplements for joint health. Start with "A" rated essentials, then add based on your arthritis type and symptoms.
          </p>

          <div className="bg-white rounded-2xl border border-border p-5 mb-6" data-testid="daily-schedule">
            <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Optimized Daily Schedule
            </h3>
            <div className="space-y-3">
              {[
                { time: 'Morning (with breakfast)', supps: 'Vitamin D3 + K2, Omega-3, Glucosamine + Chondroitin (1st dose), Turmeric', color: 'bg-amber-50 border-amber-200 text-amber-900' },
                { time: 'Midday (with lunch)', supps: 'Glucosamine + Chondroitin (2nd dose), Turmeric (2nd dose), Boswellia', color: 'bg-sky-50 border-sky-200 text-sky-900' },
                { time: 'Afternoon (empty stomach)', supps: 'SAMe, UC-II Collagen', color: 'bg-orange-50 border-orange-200 text-orange-900' },
                { time: 'Evening (before bed)', supps: 'Magnesium Glycinate, Omega-3 (2nd dose), Boswellia (2nd dose)', color: 'bg-indigo-50 border-indigo-200 text-indigo-900' }
              ].map((slot, i) => (
                <div key={i} className={`rounded-xl border p-3 ${slot.color}`}>
                  <p className="text-xs font-semibold">{slot.time}</p>
                  <p className="text-xs mt-1">{slot.supps}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <Accordion type="multiple" className="space-y-3">
              {ARTHRITIS_PROTOCOL.map((supp, index) => {
                const Icon = supp.icon;
                return (
                  <motion.div key={supp.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * index }}>
                    <AccordionItem value={supp.name} className="bg-white rounded-xl border border-border overflow-hidden">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50/50">
                        <div className="flex items-center gap-3 text-left">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${supp.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-sm text-[#1A1A1A]">{supp.name}</h3>
                              <EvidenceGrade grade={supp.evidence} />
                              <div className="flex gap-1">
                                {supp.conditions.map(c => (
                                  <span key={c} className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${c === 'OA' ? 'bg-cyan-50 text-cyan-700' : 'bg-orange-50 text-orange-700'}`}>{c}</span>
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{supp.dosage}</span>
                              <span>{supp.timing}</span>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-4 pt-2">
                          <div className="p-3 rounded-lg bg-cyan-50 border border-cyan-200">
                            <h4 className="text-sm font-medium text-cyan-900 mb-1 flex items-center gap-2">
                              <FlaskConical className="w-4 h-4" />
                              Why This Matters for Arthritis
                            </h4>
                            <p className="text-xs text-cyan-800">{supp.why}</p>
                          </div>
                          {supp.interactions.length > 0 && (
                            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                              <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                Medication Interactions
                              </h4>
                              <ul className="space-y-1">
                                {supp.interactions.map((int_, i) => (
                                  <li key={i} className="text-xs text-blue-800 flex items-start gap-2">
                                    <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    {int_}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {supp.notes && (
                            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                              <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                Clinical Note
                              </h4>
                              <p className="text-xs text-gray-700">{supp.notes}</p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                );
              })}
            </Accordion>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-primary" />
            Medication Compatibility Guide
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            How these supplements interact with common arthritis medications.
          </p>
          <div className="space-y-3" data-testid="medication-compatibility">
            {COMMON_MEDICATIONS.map((med, i) => (
              <div key={i} className="bg-white rounded-xl border border-border p-4">
                <h4 className="font-semibold text-sm text-[#1A1A1A] mb-1">{med.name}</h4>
                <p className="text-xs text-muted-foreground">{med.notes}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            Arthritis Lifestyle Strategies
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Evidence-based lifestyle modifications that complement your supplement protocol.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lifestyle-tips">
            {LIFESTYLE_TIPS.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div key={i} className="bg-white rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm text-[#1A1A1A]">{tip.title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.content}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-gradient-to-br from-cyan-50 to-teal-50 border border-cyan-200 rounded-2xl p-6 mb-10 text-center" data-testid="symptom-tracker-cta">
          <h3 className="text-lg font-bold text-cyan-900 mb-2">Track Your Joint Symptoms</h3>
          <p className="text-sm text-cyan-700 mb-4 max-w-md mx-auto">
            Log pain levels, stiffness, swelling, and flare triggers daily to identify patterns and share with your rheumatologist.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="btn-primary">
              <Link to="/symptoms">Start Symptom Tracking</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/journal">Open Journal</Link>
            </Button>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="grid grid-cols-2 gap-3 mb-10">
          <Link to="/quiz" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Pill className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Get Personalized Protocol</p>
            <p className="text-xs text-muted-foreground mt-1">Take the full assessment</p>
          </Link>
          <Link to="/ask" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Brain className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Ask AI About Arthritis</p>
            <p className="text-xs text-muted-foreground mt-1">Get supplement answers</p>
          </Link>
        </motion.div>
      </main>

      <GlobalFooter />
    </div>
  );
}
